<!-- eslint-disable vue/multi-word-component-names -->
<template>
    <div>
        <!-- Section header. The icon is its own column so the title and sub-copy share a left edge. -->
        <div class="mb-4 flex items-start gap-2">
            <i v-if="otherTitle" class="bi bi-people-fill text-sm leading-6 text-gray-900 shrink-0" aria-hidden="true"></i>
            <div class="min-w-0 flex-1">
                <!-- Title with the due date to the side. -->
                <div class="flex items-center justify-between gap-2">
                    <h2 class="scaffold-body truncate">{{ otherTitle }}</h2>
                    <span v-if="dateLabel" class="scaffold-body-small text-valence-grey-750 whitespace-nowrap shrink-0">{{ dateLabel }}</span>
                </div>
                <!-- Sub-copy (left) and Edit team (right) share the line below; both are optional. -->
                <div v-if="otherSubtitle || shouldShowEditTeamButton" class="flex items-start gap-3 mt-1">
                    <p v-if="otherSubtitle" class="scaffold-body-small text-valence-grey-750 min-w-0 whitespace-pre-line">{{ otherSubtitle }}</p>
                    <button
                        v-if="shouldShowEditTeamButton"
                        class="flex items-center gap-1.5 cursor-pointer hover:opacity-70 transition-opacity shrink-0 ml-auto"
                        @click="$emit('edit-list')">
                        <span class="body-small text-valence-primary-500 font-medium">Edit team</span>
                    </button>
                </div>
            </div>
        </div>
        <div class="flex flex-col gap-3">
            <!-- Empty state: user has opted into reviewing team members but hasn't added any yet -->
            <div
                v-if="!Object.keys(reviewsByRelationship).length"
                class="rounded-xl flex flex-col items-center justify-center gap-3 px-5 py-8 bg-white border border-dashed border-valence-grey-300 text-center">
                <i class="bi bi-people text-3xl text-valence-grey-500" aria-hidden="true"></i>
                <div class="body-small text-valence-grey-750">You haven't added any team members to review yet.</div>
                <BaseButton v-if="!isMomentReadOnly" theme="secondary" class="flex items-center gap-2" @click="$emit('edit-list')">
                    <i class="bi bi-plus-lg" aria-hidden="true"></i>
                    Add team members
                </BaseButton>
            </div>
            <!-- Grouped reviews by relationship -->
            <div v-for="(relationshipReviews, relationshipId) in reviewsByRelationship" :key="relationshipId" class="flex flex-col gap-3">
                <!-- My People Card -->
                <div class="rounded-xl flex flex-col bg-white transition-colors border border-valence-grey-200">
                    <button
                        class="flex justify-between items-start px-5 py-5 cursor-pointer hover:bg-gray-50 rounded-t-xl text-start"
                        :class="{ 'rounded-b-xl': !expandedCards[relationshipId] }"
                        :aria-expanded="!!expandedCards[relationshipId]"
                        @click="toggleCard(relationshipId)">
                        <div class="flex flex-col gap-0.5">
                            <div class="body-bold" :class="isMomentReadOnly ? 'text-valence-grey-400' : 'text-valence-grey-850'">
                                {{ relationshipReviews[0].relationship?.reviewee_name }}
                            </div>
                            <div class="body-small min-h-5" :class="isMomentReadOnly ? 'text-valence-grey-400' : 'text-valence-grey-750'">
                                {{ relationshipReviews[0].relationship?.reviewee_job_title }}
                            </div>
                        </div>
                        <div class="flex items-center gap-3">
                            <div
                                v-if="isSectionLocked || !(isMomentReadOnly && getStatus(relationshipReviews) === STATUS.NOT_STARTED)"
                                class="details rounded-2xl px-4 p-1 font-medium whitespace-nowrap"
                                :class="isSectionLocked ? 'bg-[#ECECEC] text-valence-grey-750' : getStatusColor(relationshipReviews)">
                                {{ isSectionLocked ? "Coming soon" : getStatus(relationshipReviews) }}
                            </div>
                            <i :class="expandedCards[relationshipId] ? 'bi-chevron-up' : 'bi-chevron-down'" class="bi text-gray-500 p-1" aria-hidden="true"></i>
                        </div>
                    </button>
                    <div :hidden="!expandedCards[relationshipId]" class="flex flex-col gap-1 overflow-hidden">
                        <HorizontalDivider class="my-0!" />
                        <PerformanceManagementCTA
                            v-for="review in relationshipReviews"
                            :key="review.id"
                            :review="review"
                            class="px-5"
                            :is-loading="!!currentReviewId && currentReviewId === review.id"
                            :is-disabled="!!currentReviewId && currentReviewId !== review.id"
                            :is-moment-read-only="isMomentReadOnly"
                            :is-coming-soon="isSectionLocked"
                            @cta-convo-clicked="(reviewId) => $emit('cta-convo-clicked', reviewId)"
                            @completion-toggled="(reviewId, complete) => $emit('completion-toggled', reviewId, complete)" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import { formatSectionDateLabel, isStartDateInFuture } from "~vue/composables/useCtaDateRange";
import type { DecoratedPerformanceReview } from "~vue/types/performance_management";
import { logUserInteraction } from "~vue/utils/logUtils";
import { sortBy, uniq } from "lodash";
import { computed, onMounted, ref, watch } from "vue";

import { getReviewGroupStatus, getReviewGroupStatusColor, STATUS } from "./composables/useReviewGroupStatus";
import { PERFORMANCE_REVIEW_EVENT_PREFIX } from "./constants";
import PerformanceManagementCTA from "./PerformanceManagementCTA.vue";

const props = withDefaults(
    defineProps<{
        dueDate?: string | null;
        startDate?: string | null;
        otherTitle?: string;
        otherSubtitle?: string;
        reviews: DecoratedPerformanceReview[];
        momentId?: string | number | null;
        currentReviewId?: string | null;
        isMomentReadOnly?: boolean;
    }>(),
    { dueDate: null, startDate: null, otherTitle: "", otherSubtitle: "", momentId: null, currentReviewId: null },
);

defineEmits(["edit-list", "cta-convo-clicked", "completion-toggled"]);

const dateLabel = computed(() => formatSectionDateLabel(props.startDate, props.dueDate));

// Section hasn't opened yet — disable/grey its CTAs until the start date arrives.
const isSectionLocked = computed(() => isStartDateInFuture(props.startDate));

onMounted(() => {
    if (props.reviews?.length) {
        logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}team_section_shown`, { moment_id: props.momentId });
    }
});

const getStatus = (reviews: DecoratedPerformanceReview[]) => getReviewGroupStatus(reviews);

const getStatusColor = (reviews: DecoratedPerformanceReview[]) => getReviewGroupStatusColor(reviews);

const reviewsByRelationship = computed(() => {
    const reviewsWithRelationships = props.reviews.filter((review) => !!review.relationship);
    const result: Record<string, DecoratedPerformanceReview[]> = {};
    const relationshipIds = uniq(sortBy(reviewsWithRelationships, (review) => review.relationship?.reviewee_name).map((review) => review.relationship?.id));
    for (const relationshipId of relationshipIds) {
        const relationshipReviews = reviewsWithRelationships.filter((review) => review.relationship?.id === relationshipId);
        result[String(relationshipId)] = sortBy(relationshipReviews, (review) => review.cta?.id); // TODO: Sort this by something admin-specified rather than CTA ID
    }
    return result;
});

const shouldShowEditTeamButton = computed(() => !props.isMomentReadOnly && Object.keys(reviewsByRelationship.value).length);

// Track expanded/collapsed state for each colleague card
const expandedCards = ref<Record<string | number, boolean>>({});

const toggleCard = (relationshipId: number | string) => {
    expandedCards.value[relationshipId] = !expandedCards.value[relationshipId];
    logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}team_member_card_toggled`, {
        relationship_id: relationshipId,
        new_state: expandedCards.value[relationshipId] ? "open" : "closed",
    });
};

// Initialize first colleague card as expanded when relationships load
watch(reviewsByRelationship, (data) => {
    if (data) {
        const firstRelationshipId = Object.keys(data)[0];
        if (firstRelationshipId && expandedCards.value[firstRelationshipId] === undefined) {
            expandedCards.value[firstRelationshipId] = true;
        }
    }
});
</script>
